import { useState, useEffect, useCallback } from 'react';
import Axios from 'axios';
import { getAuthHeader } from '../../../../utils/authToken';

const useReviewSession = (sessionId) => {
    const [session, setSession] = useState(null);
    const [questions, setQuestions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    // Fetch questions for the session
    const fetchQuestions = useCallback(async () => {
        if (!sessionId) return;
        
        setLoading(true);
        try {
            // Fetch all questions (you might want to implement pagination later)
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/teacher-reviews/sessions/${sessionId}/questions`,
                {
                    params: {
                        status: 'all',
                        page: 1,
                        limit: 100 // Fetch more questions at once
                    },
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            
            setQuestions(response.data.questions || []);
            setHasMore(response.data.pagination?.page < response.data.pagination?.pages);
            
            // Also update session info if provided
            if (response.data.session) {
                setSession(response.data.session);
            }
        } catch (err) {
            console.error('Error fetching questions:', err);
            if (err.response?.status === 403) {
                if (err.response.data?.message?.includes('not assigned')) {
                    setError('403: You are not assigned to this review session');
                } else if (err.response.data?.message?.includes('not accessible')) {
                    setError('403: This review session is not accessible');
                } else {
                    setError('403: Access denied to this review session');
                }
            } else if (err.response?.status === 404) {
                setError('404: Review session not found');
            } else {
                setError('Failed to load questions');
            }
        } finally {
            setLoading(false);
        }
    }, [sessionId, page]);

    // Fetch progress
    const refetchProgress = useCallback(async () => {
        if (!sessionId) return;
        
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/teacher-reviews/sessions/${sessionId}/progress`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            
            // Update session with latest progress
            setSession(prev => ({
                ...prev,
                my_progress: response.data
            }));
        } catch (err) {
            console.error('Error fetching progress:', err);
        }
    }, [sessionId]);

    // Load more questions (for pagination if needed)
    const loadMoreQuestions = useCallback(async () => {
        if (!hasMore || loading) return;
        
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/teacher-reviews/sessions/${sessionId}/questions`,
                {
                    params: {
                        status: 'all',
                        page: page + 1,
                        limit: 100
                    },
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            
            setQuestions(prev => [...prev, ...(response.data.questions || [])]);
            setPage(prev => prev + 1);
            setHasMore(response.data.pagination?.page < response.data.pagination?.pages);
        } catch (err) {
            console.error('Error loading more questions:', err);
        }
    }, [sessionId, page, hasMore, loading]);

    // Initial load
    useEffect(() => {
        if (sessionId) {
            fetchQuestions();
        }
    }, [sessionId, fetchQuestions]);

    // Auto-refresh session info every minute
    useEffect(() => {
        if (!sessionId) return;
        
        const interval = setInterval(() => {
            refetchProgress();
        }, 60000); // Refresh every minute
        
        return () => clearInterval(interval);
    }, [sessionId, refetchProgress]);

    return {
        session,
        questions,
        loading,
        error,
        hasMore,
        refetchProgress,
        loadMoreQuestions
    };
};

export default useReviewSession;